"use client";

import { useEffect, useState } from "react";
import { Check, Play } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/hooks/useStore";
import { getSchoolHistoryList } from "@/store/controller/schoolHistory.controller";
import { getVisionMissionList } from "@/store/controller/visionMission.controller";
import { VideoModal } from "./videoModal";

const statCardStyles = [
  { card: "bg-green-700 text-white", value: "", label: "text-green-200" },
  { card: "bg-yellow-400", value: "text-yellow-900", label: "text-yellow-800" },
  { card: "bg-gray-50 border border-gray-100", value: "text-gray-900", label: "text-gray-500" },
  { card: "bg-orange-50 border border-orange-100", value: "text-orange-600", label: "text-gray-500" },
];

export function ProfilSection() {
  const dispatch = useAppDispatch();
  const [videoOpen, setVideoOpen] = useState(false);
  const history = useAppSelector((state) => state.schoolHistory.list[0]);
  const visionMissionList = useAppSelector((state) => state.visionMission.list);

  useEffect(() => {
    dispatch(getSchoolHistoryList({}));
    dispatch(getVisionMissionList({}));
  }, [dispatch]);

  const visi = visionMissionList.find((item) => item.key === "visi");
  const misiItems = visionMissionList.filter((item) => item.key === "misi");

  if (!history) return null;

  return (
    <section id="tentang" className="py-20 bg-white">
      {videoOpen && <VideoModal videoUrl={history.video_url} onClose={() => setVideoOpen(false)} />}
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <span className="inline-block text-green-700 font-semibold text-sm uppercase tracking-widest mb-3">
              Profil Sekolah
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-2">{history.title}</h2>
            <div className="w-14 h-1 bg-yellow-400 rounded mb-6" />
            <p className="text-gray-600 leading-relaxed mb-8 whitespace-pre-line">{history.description}</p>

            <div className="space-y-4">
              {visi && (
                <div className="p-5 bg-green-50 rounded-2xl border border-green-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 rounded bg-green-700 flex items-center justify-center text-white text-xs font-bold">
                      V
                    </div>
                    <h4 className="font-bold text-green-900">{visi.title}</h4>
                  </div>
                  <p className="text-green-800 text-sm leading-relaxed">{visi.content}</p>
                </div>
              )}
              {misiItems.length > 0 && (
                <div className="p-5 bg-yellow-50 rounded-2xl border border-yellow-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 rounded bg-yellow-500 flex items-center justify-center text-white text-xs font-bold">
                      M
                    </div>
                    <h4 className="font-bold text-yellow-900">{misiItems[0].title}</h4>
                  </div>
                  <ul className="space-y-1.5 text-yellow-800 text-sm">
                    {misiItems.map((item) => (
                      <li key={item.id} className="flex items-start gap-2">
                        <Check size={14} className="text-yellow-500 mt-0.5 flex-shrink-0" strokeWidth={3} />
                        {item.content}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div
              className="relative rounded-3xl overflow-hidden bg-gray-900 aspect-video shadow-2xl group cursor-pointer"
              onClick={() => setVideoOpen(true)}
            >
              <img
                src={history.image}
                alt={history.title}
                className="w-full h-full object-cover opacity-60 group-hover:opacity-50 group-hover:scale-105 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div
                  className="w-18 h-18 rounded-full bg-white/90 group-hover:bg-yellow-400 flex items-center justify-center shadow-2xl transition-all duration-300 group-hover:scale-110"
                  style={{ width: 72, height: 72 }}
                >
                  <Play size={26} className="text-green-800 ml-1" fill="currentColor" />
                </div>
                <p className="text-white font-semibold mt-4 text-sm tracking-wide">Tonton Video Profil</p>
              </div>
              <div className="absolute top-4 left-4 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                Official Profile
              </div>
            </div>

            {history.stats?.length > 0 && (
              <div className="grid grid-cols-2 gap-4">
                {history.stats.map((stat, index) => {
                  const style = statCardStyles[index % statCardStyles.length];
                  return (
                    <div key={index} className={`p-5 rounded-2xl text-center ${style.card}`}>
                      <div className={`text-3xl font-extrabold mb-1 ${style.value}`}>{stat.value}</div>
                      <div className={`text-sm font-medium ${style.label}`}>{stat.label}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
